/** @jsxImportSource @emotion/react */
import { ChangeEvent, FC, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { Buttons, TextFields } from '@zip/business-components';
import * as Icons from '@zip/icons';
import { useMerchantApplicationContext, useSnackbar } from 'contexts';
import { StepComponentProps } from 'types/interfaces/sign-up/MerchantApplication.types';
import { isFormError } from 'types/common/ApiHelpers';
import { useSignUpFunctions } from 'utils';
import * as formStyles from './SignUpForm.styles';
import * as styles from './UserAccountCreationModule.styles';

export const UserAccountCreationModule: FC<StepComponentProps> = ({
  onBack,
}) => {
  const Snackbar = useSnackbar();
  const { nextSignUpStep, merchantApplicationId } =
    useMerchantApplicationContext();
  const { createUserAsync, mapServerError } = useSignUpFunctions();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState<{
    password?: string;
    confirmPassword?: string;
  }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const hasStartedTypingPassword = password.length > 0;

  const passwordRules = useMemo(
    () => ({
      minLength: password.length >= 8,
      mixedCaseAndNumber:
        /[a-z]/.test(password) && /[A-Z]/.test(password) && /\d/.test(password),
      specialCharacter: /[!@#$%^&*]/.test(password),
    }),
    [password]
  );

  const validate = () => {
    const nextErrors: {
      password?: string;
      confirmPassword?: string;
    } = {};

    const allRulesPass = Object.values(passwordRules).every(Boolean);

    if (!password) {
      nextErrors.password = 'Please enter your password';
    } else if (!allRulesPass) {
      nextErrors.password =
        'Please choose a password that meets all requirements';
    }

    if (!confirmPassword) {
      nextErrors.confirmPassword = 'Please confirm your password';
    } else if (password !== confirmPassword) {
      nextErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(nextErrors);

    return Object.keys(nextErrors).length === 0;
  };

  const handleCreateAccount = async () => {
    if (!merchantApplicationId || isSubmitting) {
      return;
    }

    if (!validate()) {
      return;
    }

    setIsSubmitting(true);

    try {
      await createUserAsync(merchantApplicationId, password);
      nextSignUpStep();
    } catch (err) {
      const mapped = mapServerError(err);
      if (isFormError(mapped)) {
        Snackbar.error(mapped.message);
      } else {
        const fieldErrors = mapped.fieldErrors;
        setErrors({
          password: fieldErrors.password,
          confirmPassword: fieldErrors.confirmPassword,
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePasswordChange = (e: ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
    if (errors.password) {
      setErrors((current) => ({ ...current, password: undefined }));
    }
  };

  const handleConfirmPasswordChange = (e: ChangeEvent<HTMLInputElement>) => {
    setConfirmPassword(e.target.value);
    if (errors.confirmPassword) {
      setErrors((current) => ({ ...current, confirmPassword: undefined }));
    }
  };

  const requirementItems = [
    {
      key: 'minLength',
      label: 'Minimum of 8 characters',
      satisfied: passwordRules.minLength,
    },
    {
      key: 'mixedCaseAndNumber',
      label:
        'Must include lower case (a-z), upper case (A-Z) and numbers (0-9)',
      satisfied: passwordRules.mixedCaseAndNumber,
    },
    {
      key: 'specialCharacter',
      label: 'Must contain special characters (!@#$%^&*)',
      satisfied: passwordRules.specialCharacter,
    },
  ];

  return (
    <Box css={styles.container}>
      <Box css={styles.formFields}>
        <TextFields.Outlined
          name="password"
          label="Enter password"
          type={showPassword ? 'text' : 'password'}
          css={[formStyles.textfield, styles.passwordField]}
          className="body2 fs-mask"
          fullWidth
          value={password}
          onChange={handlePasswordChange}
          error={Boolean(errors.password)}
          helperText={errors.password || undefined}
          endIcon={
            <button
              type="button"
              css={styles.iconButton}
              onClick={() => setShowPassword((current) => !current)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? (
                <Icons.EyeStrike width="20" height="20" css={styles.eyeIcon} />
              ) : (
                <Icons.Eye width="20" height="20" css={styles.eyeIcon} />
              )}
            </button>
          }
        />

        <Box css={styles.requirementsCard}>
          <ul css={styles.requirementsList}>
            {requirementItems.map((item) => (
              <li key={item.key} css={styles.requirementItem}>
                {!hasStartedTypingPassword ? (
                  <span
                    css={styles.requirementPendingDot}
                    aria-hidden="true"
                    data-testid={`${item.key}-pending`}
                  />
                ) : item.satisfied ? (
                  <span css={styles.requirementIconBase}>
                    <Icons.CheckCircle
                      width="20"
                      height="20"
                      fill={styles.requirementIconSuccessColor}
                      data-testid={`${item.key}-success`}
                    />
                  </span>
                ) : (
                  <span css={styles.requirementIconBase}>
                    <Icons.WarningCircle
                      width="20"
                      height="20"
                      fill={styles.requirementIconErrorColor}
                      data-testid={`${item.key}-error`}
                    />
                  </span>
                )}
                <span
                  className="body4"
                  css={styles.requirementText(item.satisfied)}
                >
                  {item.label}
                </span>
              </li>
            ))}
          </ul>
        </Box>

        <TextFields.Outlined
          name="confirmPassword"
          label="Confirm password"
          type={showConfirmPassword ? 'text' : 'password'}
          css={[formStyles.textfield, styles.passwordField]}
          className="body2 fs-mask"
          fullWidth
          value={confirmPassword}
          onChange={handleConfirmPasswordChange}
          error={Boolean(errors.confirmPassword)}
          helperText={errors.confirmPassword || undefined}
          endIcon={
            <button
              type="button"
              css={styles.iconButton}
              onClick={() => setShowConfirmPassword((current) => !current)}
              aria-label={
                showConfirmPassword
                  ? 'Hide confirm password'
                  : 'Show confirm password'
              }
            >
              {showConfirmPassword ? (
                <Icons.EyeStrike width="20" height="20" css={styles.eyeIcon} />
              ) : (
                <Icons.Eye width="20" height="20" css={styles.eyeIcon} />
              )}
            </button>
          }
        />
      </Box>

      <Box css={styles.actions}>
        <Buttons.Primary
          type="button"
          variant="text"
          css={styles.backButton}
          onClick={onBack}
          disabled={isSubmitting}
        >
          Back
        </Buttons.Primary>

        <Buttons.Primary
          variant="contained"
          css={styles.primaryButton}
          onClick={handleCreateAccount}
          disabled={isSubmitting}
        >
          Create account
        </Buttons.Primary>
      </Box>
    </Box>
  );
};

export default UserAccountCreationModule;
